/**
 * RUN T — T-1. The pure half of the sidebar patient typeahead.
 *
 * Kept out of the component so the rules that matter — when we query, how
 * many rows we show, what the keyboard does, and what a row reads like — are
 * testable without a DOM and cannot drift behind a re-render.
 */

/** One row of the dropdown. Every field is already decrypted server-side. */
export interface PatientSuggestion {
  id: string;
  name: string;
  mrn: string;
  dateOfBirth: string | null;
  age: number | null;
  /** epoch ms of the most recent past appointment, or null. */
  lastAppointmentAt: number | null;
  /** epoch ms of the soonest future appointment, or null. */
  nextAppointmentAt: number | null;
  status: PatientStatus;
}

/**
 * The status pill.
 *
 * `do_not_contact` exists in the type because the brief asks the row to show
 * it when the PracticeHub record carries it. **No PracticeHub field carries
 * it today**: the sync maps first_name, last_name, dob, email, phone, mrn and
 * sex and nothing else, `patient_mappings` holds only ids and an MRN, and
 * there is no raw-payload table. So the value is never produced, and the
 * brief's own "else nothing" is what actually renders. The moment the sync
 * stores a contact preference, one line in the endpoint lights this up and
 * the UI already knows what to do with it.
 *
 * Incident I-1 Part B — where it WILL come from (recorded for the v3
 * migration, not built): PracticeHub v3's patient resource carries
 * `communication_status`, an integer set from the patient file — 1 normal
 * contact, 2 marketing only, 3 DO NOT CONTACT — read-only through the API and
 * usable as a list filter (docs: ~/Desktop/ph-v3-docs, api-reference
 * patients). v3 also has `patient_status` (active / archived / deceased). The
 * legacy /api this account uses exposes neither, and v3 is not enabled on the
 * account (404 not_available, 09-18). So: `do_not_contact` ⇐
 * communication_status === 3, the day the sync moves to v3.
 */
export type PatientStatus = 'active' | 'do_not_contact' | null;

/** The dropdown never grows past this, however many the search matched. */
export const MAX_SUGGESTIONS = 8;

/** Below this, typing is still a thought rather than a query. */
export const MIN_QUERY_LENGTH = 2;

/** Long enough that a fast typist sends one request, not eight. */
export const DEBOUNCE_MS = 250;

export type TypeaheadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'results'; results: PatientSuggestion[] }
  | { kind: 'empty' }
  | { kind: 'error' };

/** Do we ask the server about this text at all? */
export function shouldQuery(text: string): boolean {
  return text.trim().length >= MIN_QUERY_LENGTH;
}

/** The state a response maps to. Keeps 'empty' distinct from 'idle'. */
export function stateForResults(results: PatientSuggestion[]): TypeaheadState {
  return results.length > 0
    ? { kind: 'results', results: results.slice(0, MAX_SUGGESTIONS) }
    : { kind: 'empty' };
}

/**
 * Arrow-key movement. -1 means "nothing highlighted", which is the state
 * Enter uses to fall back to the Patients page. Down from nothing selects the
 * first row; up from the first row returns to nothing rather than wrapping
 * onto the last, so a clinician can always get back to their raw text.
 */
export function nextIndex(current: number, length: number, delta: 1 | -1): number {
  if (length === 0) return -1;
  const n = current + delta;
  if (n < -1) return length - 1;
  if (n >= length) return -1;
  return n;
}

/** Is this the ⌘K / Ctrl-K chord? */
export function isFocusChord(e: { key: string; metaKey: boolean; ctrlKey: boolean }): boolean {
  return e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey);
}

/** Where Enter goes when no row is highlighted — today's behaviour, kept. */
export function fallbackPath(text: string): string {
  return `/patients?search=${encodeURIComponent(text.trim())}`;
}

/** Where a selected row goes. */
export function patientPath(id: string): string {
  return `/patients/${id}`;
}

/** dd MMM yyyy, or an em dash. Never throws on a bad value. */
export function formatDate(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "12 Mar 1979 · 47" — the identity line under the name. */
export function identityLine(s: Pick<PatientSuggestion, 'dateOfBirth' | 'age'>): string {
  const dob = formatDate(s.dateOfBirth);
  return s.age === null ? dob : `${dob} · ${s.age}`;
}

// ── Incident I-1 Part B — the PracticeHub-style result card ─────────────────

/** The three date formats a clinic can choose in Settings → Clinic. */
export type ClinicDateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/** Anything unrecognised falls back to the Cyprus default, DD/MM/YYYY. */
export function normaliseDateFormat(value: unknown): ClinicDateFormat {
  return value === 'MM/DD/YYYY' || value === 'YYYY-MM-DD' ? value : 'DD/MM/YYYY';
}

function toDate(value: number | string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // A bare YYYY-MM-DD (a date of birth) is a calendar date, not an instant:
  // read it at local noon so no timezone can move it to the day before.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y!, m! - 1, d!, 12);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A date in the clinic's chosen format, or an em dash. Never throws. */
export function formatClinicDate(value: number | string | null | undefined, fmt: ClinicDateFormat): string {
  const d = toDate(value);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  if (fmt === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
  if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
  return `${dd}/${mm}/${yyyy}`;
}

/** "Mon 15/09/2026": the short weekday in the UI language, then the clinic date. */
export function formatApptDate(value: number | string | null | undefined, fmt: ClinicDateFormat, lang = 'en'): string {
  const d = toDate(value);
  if (!d) return '—';
  let weekday: string;
  try {
    weekday = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d);
  } catch {
    weekday = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(d);
  }
  return `${weekday} ${formatClinicDate(d.getTime(), fmt)}`;
}

/**
 * The name split into plain and matched segments, so the typed text renders
 * in bold without HTML injection. Case-insensitive; every occurrence.
 */
export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const q = query.trim();
  if (!q) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const parts: Array<{ text: string; match: boolean }> = [];
  let i = 0;
  for (;;) {
    const at = lower.indexOf(needle, i);
    if (at === -1) break;
    if (at > i) parts.push({ text: text.slice(i, at), match: false });
    parts.push({ text: text.slice(at, at + needle.length), match: true });
    i = at + needle.length;
  }
  if (i < text.length) parts.push({ text: text.slice(i), match: false });
  return parts.length ? parts : [{ text, match: false }];
}

/** The card is never narrower than this — the brief's floor. */
export const MIN_CARD_WIDTH = 480;
/** ...and prefers this when the window allows. */
export const PREFERRED_CARD_WIDTH = 560;
const CARD_GAP = 6;
const VIEWPORT_MARGIN = 8;

/**
 * Where the floating card goes: under the search box, left edges aligned, at
 * least 480 px wide (it deliberately spills past the sidebar over the main
 * content), shifted left only if the window is too narrow to fit it.
 */
export function cardPosition(
  anchor: { left: number; bottom: number; width: number },
  viewport: { width: number; height: number },
): { left: number; top: number; width: number; maxHeight: number } {
  const room = viewport.width - 2 * VIEWPORT_MARGIN;
  const width = Math.min(Math.max(PREFERRED_CARD_WIDTH, anchor.width), Math.max(MIN_CARD_WIDTH, room));
  let left = anchor.left;
  if (left + width > viewport.width - VIEWPORT_MARGIN) left = Math.max(VIEWPORT_MARGIN, viewport.width - VIEWPORT_MARGIN - width);
  const top = anchor.bottom + CARD_GAP;
  const maxHeight = Math.max(160, viewport.height - top - VIEWPORT_MARGIN);
  return { left, top, width, maxHeight };
}
